// ─────────────────────────────────────────────────────────────────────────────
// utils/kitchenPwa.ts — die BILDSCHIRM-ANSICHTEN als eigenstaendige Anwendung
// je Kanal (T-M12.12 — REQ-SC-076, REQ-SC-078, REQ-OPS-002)
// … und seit T-M12.44 die Registrierung des Dienst-Arbeiters fuer ALLE DREI
// Bildschirm-Ansichten (REQ-OV-009, REQ-SC-006, REQ-SC-007)
// … und seit T-M12.46 das je Kanal erzeugte Manifest ebenfalls fuer ALLE DREI
// (REQ-SC-076 Praez. 2, REQ-SC-060, REQ-OV-009).
//
// ⚠ DER DATEINAME IST ENGER ALS DER INHALT — und das ist Absicht. Diese Datei
// traegt seit T-M12.44 auch den Abholscreen und die Fast-Line-Ansicht. Sie
// umzubenennen hiesse, den Import in `views/KitchenView.vue` anzufassen; genau
// das ist in T-M12.44 untersagt (dort arbeitet T-M12.5 gleichzeitig), und
// T-M12.46 fuehrt `views/KitchenView.vue` ausdruecklich NICHT unter seinen
// beruehrten Dateien.
//
// ── WAS T-M12.46 GEAENDERT HAT — UND WAS NICHT ──────────────────────────────
// Bis zum 2026-08-11 galt an `/abholscreen/:printerId` und `/fastline/:printerId`
// das STATISCHE Manifest aus `vite.config.ts`: wer von dort installierte, bekam
// die Anwendung „Printerque Kitchen TV" mit dem Geltungsbereich `/` — am
// gebauten `dist/manifest.webmanifest` nachgemessen. An einem Standort mit
// mehreren Kanaelen standen damit mehrere gleichnamige Symbole nebeneinander,
// und das eine, das der GAST sieht, hiess wie das, das die KUECHE sieht.
// REQ-SC-076 Praez. 2 schliesst diese Luecke; der Bestand fuehrt das
// Gegenstueck seit jeher (`onlinequeue-main/app/api/queue/[uuid]/manifest/
// route.ts`, `name: 'Abholscreen - ${printer.name}'`).
//
// ES ENTSTEHT EINE BILDUNGSSTELLE MIT EINEM PARAMETER, KEINE ZWEITE KOPIE VON
// `kanalManifest` — so ausdruecklich verlangt (Umfang (a)); der Ansichtsteil
// steht in EINER Tabelle (`BILDSCHIRM_ANSICHTSFORM`), und was daran je Ansicht
// verschieden ist, ist die BEZEICHNUNG und die ADRESSE, sonst nichts
// (REQ-SC-085: Fast Line ist eine Darstellungsvariante des Abholscreens und
// kein viertes Objekt).
//
// REQ-SC-076: „Die Ansicht bindet ein Anwendungs-Manifest, das den Kanalnamen
// traegt und als Startadresse die Adresse genau dieses Kanals nennt (Vollbild,
// Querformat), und registriert einen eigenen Dienst-Arbeiter, der die
// Anwendungshuelle vorhaelt. Der Dienst-Arbeiter haelt keine Auftragsdaten vor."
//
// ── WAS VORHER WAR UND WARUM DAS NICHT REICHTE ──────────────────────────────
// Bis zum 2026-08-11 gab es genau EIN statisches Manifest (`vite.config.ts`).
// Es heisst „Printerque Kitchen TV", nennt aber KEINEN Kanal — und seit
// T-M12.36 auch keine Startadresse (`start_url: undefined`), weil jede fest
// nennbare Adresse auf einem Kuechengeraet im Verwaltungs-Login endet. Ohne
// Startadresse gilt die Adresse des Dokuments, aus dem installiert wurde: im
// Regelfall die Kanaladresse, bei einer Installation aus `/jobs` heraus aber
// `/jobs`. Die WIRKUNG von REQ-SC-076 war damit im Regelfall hergestellt, die
// Anforderung selbst nicht — sie sagt „nennt", und ein fehlender Schluessel
// nennt nichts.
//
// ── DIE BAUFORM: EINE REINE FUNKTION, KEIN SERVERSEITIGES ERZEUGNIS ─────────
// Die Alt-Loesung erzeugt das Manifest je Drucker serverseitig
// (`onlinequeue-main/app/api/kitchen/[uuid]/manifest/route.ts`). Printerque ist
// eine statisch ausgelieferte Einzelseiten-Anwendung; eine eigene Function dafuer
// waere eine neue Auslieferungseinheit fuer eine reine Zeichenkette. `kanalManifest`
// ist deshalb eine REINE FUNKTION: sie bekommt Kanalschluessel, Kanalname und
// Ursprung und liefert das fertige Manifest. Die Ansicht haengt es zur Laufzeit
// als Blob an den `<link rel="manifest">` (siehe `setzeKanalManifest`).
//
// ⚠ WARUM VOLLSTAENDIGE ADRESSEN UND NICHT `/kitchen/<id>` — DAS IST KEINE
// GESCHMACKSFRAGE: die Adressen eines Manifests werden gegen die Adresse DES
// MANIFESTS aufgeloest. Ein Blob-Verweis (`blob:https://…/uuid`) hat nach dem
// URL-Standard einen undurchsichtigen Pfad; ein Verweis wie `/kitchen/abc`
// laesst sich dagegen NICHT aufloesen und faellt still aus dem Manifest heraus.
// Deshalb traegt jede Adresse hier ihren Ursprung mit. Der Waechter prueft sie
// ueber ihren Pfadanteil gegen die Routen des Routers.
//
// ── WAS DER DIENST-ARBEITER VORHAELT — UND WAS NICHT (REQ-SC-078) ───────────
// Er haelt die ANWENDUNGSHUELLE vor (die vom Bau erzeugten Dateien: JS, CSS,
// HTML, Schriften, Bilder — `workbox.globPatterns` in `vite.config.ts`). Er
// haelt KEINE Auftragsdaten vor: die Auftragslage kommt live aus Firestore
// (`useScreenChannel`), und ein zwischengespeicherter Auftragsstand waere an
// einem Kuechenbildschirm eine Falschaussage. Bewacht in
// `dashboard/test/routeReachability.test.ts`, Abschnitt 6c.
//
// ⚠ DIESE REGEL GILT FUER ALLE DREI ANSICHTEN GLEICH — und zwar nicht, weil
// sie dreimal beschlossen wurde, sondern weil es DASSELBE Skript ist: alle
// drei Registrierungen laden `/sw.js` aus derselben Bau-Konfiguration. Der
// Grund traegt aber auch fachlich: Abholscreen und Fast Line zeigen ebenfalls
// eine Auftragslage (der Abholscreen die Nummern, die Fast Line die Antwort
// auf einen Scan), und ein zwischengespeicherter Stand waere dort dieselbe
// Falschaussage — vor dem Gast sogar eine sichtbarere.
// ─────────────────────────────────────────────────────────────────────────────

/** Die vom Bau erzeugte Datei des Dienst-Arbeiters (`vite-plugin-pwa`). */
export const KITCHEN_SW_URL = '/sw.js';

/**
 * Geltungsbereich des Dienst-Arbeiters — BEWUSST NUR die Kitchen-TV-Adressen.
 *
 * Er ist enger als der Ablageort von `/sw.js` (das waere `/`) und deshalb ohne
 * `Service-Worker-Allowed`-Kopfzeile zulaessig. Damit erfuellt die Registrierung
 * Akzeptanzkriterium 3 nicht bloss der Stelle nach („nur in der Kitchen-TV-
 * Ansicht gerufen"), sondern der WIRKUNG nach: der Dienst-Arbeiter steuert
 * ausschliesslich Seiten unter `/kitchen/…` und beruehrt die Verwaltungsansichten
 * auch dann nicht, wenn jemand sie im selben Browser oeffnet.
 */
export const KITCHEN_SW_SCOPE = '/kitchen/';

/**
 * Das letzte Adressglied der LIGHT-ADRESSE (T-M25.1, REQ-SC-103 (1)).
 *
 * `/kitchen/<kanal>/light` — bewusst INNERHALB von `/kitchen/`. Eine Adresse
 * `/kitchen-light/<kanal>` haette denselben Zweck erfuellt und einen VIERTEN
 * Bildschirm-Geltungsbereich eroeffnet (und damit einen zweiten
 * Dienst-Arbeiter-Bereich); sie ist erwogen und aus genau diesem Grund
 * verworfen (REQ-SC-103 (2), REQ-SC-076, REQ-OPS-057 (b)).
 *
 * Er steht HIER, weil hier der Adressraum der Bildschirm-Ansichten gefuehrt
 * wird; die Rechnung „welche Adresse traegt welchen Modus" liegt in
 * `utils/screenModeRoute.ts` und liest diese Konstante, statt sie noch einmal
 * zu schreiben.
 */
export const LIGHT_SEGMENT = 'light';

/** Geltungsbereich des Dienst-Arbeiters am Abholscreen (T-M12.44). */
export const ABHOLSCREEN_SW_SCOPE = '/abholscreen/';

/** Geltungsbereich des Dienst-Arbeiters an der Fast-Line-Ansicht (T-M12.44). */
export const FASTLINE_SW_SCOPE = '/fastline/';

/**
 * Die Geltungsbereiche der drei Bildschirm-Ansichten — DREI STATT EINEM,
 * und das ist die begruendete Wahl von T-M12.44.
 *
 * ── WARUM NICHT EIN EINZIGES `/` ────────────────────────────────────────────
 * `/kitchen/`, `/abholscreen/` und `/fastline/` haben unterhalb von `/` kein
 * gemeinsames Praefix; ein Geltungsbereich, der alle drei deckt, kann nur `/`
 * sein. `/` zoege aber die VERWALTUNGSOBERFLAECHE wieder in den Dienst-Arbeiter
 * hinein — und ob die eine PWA sein soll, ist ein Entscheid des Auftraggebers
 * (OP-132) und ausdruecklich NICHT im Umfang dieses Tasks. Eine Bauform, die
 * den Entscheid vorwegnimmt, waere ein Entscheid.
 *
 * Zwei weitere Gruende, die auch ohne OP-132 taeten:
 * • `/` zoege die Verwaltungsansichten in die je Kanal INSTALLIERTE Anwendung
 *   hinein — genau das, was REQ-SC-076 mit `scope: /kitchen/` ausschliesst.
 * • Ein Geltungsbereich `/` haelte an einem Verwaltungsrechner die Huelle vor,
 *   an dem sie niemand bestellt hat; der Preis der drei Registrierungen ist
 *   dagegen gering: es ist DASSELBE Skript (`/sw.js`), derselbe Vorrat (der
 *   Zwischenspeicher haengt am Ursprung, nicht an der Registrierung) und
 *   dieselbe Bau-Konfiguration.
 *
 * Alle drei liegen unterhalb des Ablageorts von `/sw.js` und brauchen deshalb
 * keine `Service-Worker-Allowed`-Kopfzeile (die Firebase Hosting hier nicht
 * setzt).
 */
export const BILDSCHIRM_SW_SCOPES = [
  KITCHEN_SW_SCOPE,
  ABHOLSCREEN_SW_SCOPE,
  FASTLINE_SW_SCOPE,
] as const;

export type BildschirmSwScope = typeof BILDSCHIRM_SW_SCOPES[number];

/**
 * Die Bildschirm-Ansichten (REQ-SC-076 Praez. 2, T-M12.46).
 *
 * Sie sind hier AUFGEZAEHLT und nicht als freie Zeichenkette gefuehrt: eine
 * weitere Ansicht muss in `BILDSCHIRM_ANSICHTSFORM` eine Bezeichnung und einen
 * Geltungsbereich BEKOMMEN, statt still den Kitchen TV zu erben. Genau das ist
 * der Befund, der T-M12.46 ausgeloest hat.
 *
 * ⚠ SEIT DEM 2026-08-22 SIND ES VIER (T-M25.1, REQ-SC-103 (3)) — berichtigt
 * statt geloescht (REQ-OPS-036 Praez. 6). Bis dahin stand hier „Die DREI
 * Bildschirm-Ansichten … eine VIERTE Ansicht muss …". Hinzugekommen ist
 * `kitchenLight`, die Adresse `/kitchen/:printerId/light`.
 *
 * ⚠ SIE IST EINE VIERTE BEZEICHNUNG UND KEIN VIERTER GELTUNGSBEREICH. Der
 * Light-Modus ist keine eigene Ansicht mit eigenem Quelltext: es ist
 * `views/KitchenView.vue` an einer zweiten Adresse (REQ-SC-103 (1)). Sein
 * Dienst-Arbeiter ist deshalb unveraendert der des Kitchen TV (`/kitchen/`,
 * REQ-SC-103 (2)) — `BILDSCHIRM_SW_SCOPES` bleibt bei DREI, und es entsteht
 * keine vierte Registrierung (REQ-OPS-057 (b)).
 */
export const BILDSCHIRM_ANSICHTEN = [
  'kitchen', 'kitchenLight', 'abholscreen', 'fastline',
] as const;

export type BildschirmAnsicht = typeof BILDSCHIRM_ANSICHTEN[number];

/** Was an einer Bildschirm-Ansicht ihr eigenes ist: Bezeichnung und Adresse. */
export interface BildschirmAnsichtsForm {
  /**
   * Der Geltungsbereich des DIENST-ARBEITERS.
   *
   * ⚠ ER IST NICHT MEHR DERSELBE WIE DER DES MANIFESTS (T-M25.1) — bis zum
   * 2026-08-22 hiess dieses Feld `scope` und trug beides zugleich, weil es
   * zusammenfiel. Mit der Light-Adresse faellt es auseinander: sie teilt den
   * Dienst-Arbeiter mit dem Kitchen TV (`/kitchen/`, REQ-SC-103 (2)) und hat
   * ihren eigenen Manifest-Geltungsbereich (REQ-SC-103 (3)).
   */
  swScope: BildschirmSwScope;
  /** Die Adresse dieser Ansicht an genau einem Kanal. */
  pfad(printerId: string): string;
  /**
   * Der Geltungsbereich des MANIFESTS (ohne Ursprung, REQ-SC-076 Praez. 2 (iv)).
   *
   * Fuer die drei Ansichten mit eigenem Adressraum ist es ihr Adressraum; fuer
   * die Light-Adresse ist es die Adresse selbst (REQ-SC-103 (3): „Startadresse
   * und Geltungsbereich auf der Light-Adresse dieses Kanals"). Ein `/kitchen/`
   * zoege dort die NORMALE Kitchen-TV-Adresse desselben Kanals in die
   * installierte Light-Anwendung hinein — zwei Anwendungen mit demselben
   * Geltungsbereich sind auf dem Geraet nicht auseinanderzuhalten.
   */
  manifestScope(printerId: string): string;
  /** Der volle Anwendungsname (Installationsdialog, Fensterleiste). */
  name(anzeige: string): string;
  /** Die Beschriftung UNTER dem Symbol auf dem Startbildschirm. */
  shortName(anzeige: string): string;
}

/**
 * Die Bezeichnungen der drei Ansichten — EINE Tabelle, kein zweiter Bau.
 *
 * ── `name`: DER BESTAND, 1:1 (REQ-SC-060) ──────────────────────────────────
 * Kitchen TV und Abholscreen tragen woertlich die Namensform der Alt-Loesung
 * (`app/api/kitchen/[uuid]/manifest/route.ts`: `Kitchen TV - <Name>` /
 * `Kitchen <Name>`; `app/api/queue/[uuid]/manifest/route.ts`:
 * `Abholscreen - <Name>`). Die Fast-Line-Ansicht hat im Bestand kein eigenes
 * Manifest — sie ist dort ein Modus des Abholscreens; ihre Bezeichnung folgt
 * deshalb derselben Form (`Fast Line - <Name>`).
 *
 * ── `shortName`: EINE BENANNTE BESTANDSABWEICHUNG, UND ZWAR NUR BEI FAST LINE
 * Der Bestand setzt am Abholscreen `short_name: printer.name` — der blosse
 * Kanalname. Das bleibt hier unveraendert. Fuer Fast Line waere derselbe Wert
 * jedoch GENAU DER SCHADEN, gegen den dieser Task geschrieben ist: Abholscreen
 * und Fast Line desselben Kanals koennen auf DEMSELBEN Kiosk-Geraet installiert
 * sein (Fast Line ist ein Modus des Abholscreens, REQ-SC-085), und zwei Symbole
 * mit derselben Beschriftung sind auf dem Startbildschirm nicht unterscheidbar.
 * Deshalb `Fast Line <Name>`.
 *
 * ── DIE SPRACHFRAGE, GESTELLT UND ENTSCHIEDEN (Umfang (d)) ─────────────────
 * DIE BEZEICHNUNGEN SIND FEST DEUTSCH UND LAUFEN NICHT UEBER vue-i18n. Drei
 * Gruende, und keiner davon ist Bequemlichkeit:
 *  (1) Das Manifest entsteht VOR jeder Sprachwahl. Die Sprache der Oberflaeche
 *      haengt an `localStorage`/`navigator.language` (`@/i18n/index.ts`); das
 *      Manifest wird beim ersten Rendern gebildet und beim INSTALLIEREN einmal
 *      gelesen. Ein uebersetzter Name benaehme sich also nach dem Zufall der
 *      zuletzt gewaehlten Sprache.
 *  (2) Eine installierte Anwendung, die sich still umbenennt, sobald jemand die
 *      Oberflaechensprache umstellt, ist auf einem Kiosk-Geraet eine
 *      Verwechslungsquelle — dieselbe, die dieser Task beendet.
 *  (3) „Kitchen TV", „Abholscreen" und „Fast Line" sind PRODUKTBEZEICHNUNGEN
 *      aus dem Bestand und stehen so in `/spec`; der Kanalname daneben ist ein
 *      Eigenname des Betreibers. Beides ist kein uebersetzter Satz.
 * Das ist dieselbe Linie wie beim fehlenden `description` (siehe unten) und
 * kein Widerspruch zu REQ-OV-002: dort geht es um benutzergerichtete PROSA in
 * der Oberflaeche, hier um den Eigennamen einer Anwendung.
 *
 * ── DIE VIERTE BEZEICHNUNG (2026-08-22, T-M25.1, REQ-SC-103 (3)) ────────────
 * `Kitchen TV Light - <Kanalname>` mit dem Kurznamen `Light <Kanalname>`. Sie
 * folgt der Form des Kitchen TV und ist von ihm unterscheidbar — Kitchen TV
 * und Kitchen TV Light DESSELBEN Kanals koennen auf demselben Kiosk-Geraet
 * liegen, und zwei gleich beschriftete Symbole sind dort nicht auseinander-
 * zuhalten (derselbe Grund wie bei Abholscreen/Fast Line, Nachtrag (v)).
 * Fest deutsch wie die drei uebrigen (Nachtrag (vi)), Symbole wie die drei
 * uebrigen (Nachtrag (vii)).
 */
export const BILDSCHIRM_ANSICHTSFORM: Record<BildschirmAnsicht, BildschirmAnsichtsForm> = {
  kitchen: {
    swScope:       KITCHEN_SW_SCOPE,
    pfad:          (printerId) => `${KITCHEN_SW_SCOPE}${printerId}`,
    manifestScope: () => KITCHEN_SW_SCOPE,
    name:          (anzeige) => `Kitchen TV - ${anzeige}`,
    shortName:     (anzeige) => `Kitchen ${anzeige}`,
  },
  kitchenLight: {
    // DERSELBE Dienst-Arbeiter wie der Kitchen TV (REQ-SC-103 (2)) …
    swScope:       KITCHEN_SW_SCOPE,
    // … und eine eigene Adresse INNERHALB von `/kitchen/` (REQ-SC-103 (1)).
    pfad:          (printerId) => `${KITCHEN_SW_SCOPE}${printerId}/${LIGHT_SEGMENT}`,
    manifestScope: (printerId) => `${KITCHEN_SW_SCOPE}${printerId}/${LIGHT_SEGMENT}`,
    name:          (anzeige) => `Kitchen TV Light - ${anzeige}`,
    shortName:     (anzeige) => `Light ${anzeige}`,
  },
  abholscreen: {
    swScope:       ABHOLSCREEN_SW_SCOPE,
    pfad:          (printerId) => `${ABHOLSCREEN_SW_SCOPE}${printerId}`,
    manifestScope: () => ABHOLSCREEN_SW_SCOPE,
    name:          (anzeige) => `Abholscreen - ${anzeige}`,
    shortName:     (anzeige) => anzeige,
  },
  fastline: {
    swScope:       FASTLINE_SW_SCOPE,
    pfad:          (printerId) => `${FASTLINE_SW_SCOPE}${printerId}`,
    manifestScope: () => FASTLINE_SW_SCOPE,
    name:          (anzeige) => `Fast Line - ${anzeige}`,
    shortName:     (anzeige) => `Fast Line ${anzeige}`,
  },
};

/** Der Adressraum einer Bildschirm-Ansicht — eine Stelle, nicht vier. */
export function bildschirmPfad(ansicht: BildschirmAnsicht, printerId: string): string {
  return BILDSCHIRM_ANSICHTSFORM[ansicht].pfad(printerId);
}

/** Der Adressraum des Kitchen TV — unveraendert, ein Sonderfall von oben. */
export function kitchenPfad(printerId: string): string {
  return bildschirmPfad('kitchen', printerId);
}

/** Ein Symbol des Manifests. */
export interface ManifestIcon {
  src:      string;
  sizes:    string;
  type:     string;
  purpose?: string;
}

/**
 * Das je Kanal erzeugte Anwendungs-Manifest.
 *
 * Die Schluessel sind hier AUSDRUECKLICH aufgezaehlt und nicht als
 * `Record<string, unknown>` gefuehrt: der Waechter teilt jeden Schluessel ein
 * (Adresse / Ressource / beschreibend), und ein neu hinzugefuegter faellt dort
 * auf, statt ungeprueft mitzulaufen.
 */
export interface KanalManifest {
  id:               string;
  name:             string;
  short_name:       string;
  start_url:        string;
  scope:            string;
  display:          'fullscreen';
  display_override: Array<'fullscreen' | 'standalone'>;
  orientation:      'landscape';
  lang:             'de-CH';
  theme_color:      string;
  background_color: string;
  icons:            ManifestIcon[];
}

export interface KanalManifestEingabe {
  /** Der Kanalschluessel aus der Route `/<ansicht>/:printerId`. */
  printerId: string;
  /** `printers.name` — der Kanalname, wie ihn der Betreiber vergeben hat. */
  name?: string | null;
  /** Ursprung der Auslieferung, z. B. `https://printerque-prod.web.app`. */
  origin: string;
  /**
   * Die Bildschirm-Ansicht, fuer die das Manifest gilt (T-M12.46).
   *
   * ⚠ OHNE ANGABE GILT `kitchen`, UND DAS IST EINE BEWUSSTE, ENG BEGRENZTE
   * WAHL: `views/KitchenView.vue` ist die einzige Aufrufstelle, die es vor
   * T-M12.46 gab, und dieser Task fuehrt sie ausdruecklich NICHT unter seinen
   * beruehrten Dateien (Akzeptanzkriterium 2: „am Kitchen TV ist nichts
   * veraendert"). Der Rueckfall ist deshalb der HISTORISCHE Fall und nicht die
   * bequeme Vorgabe fuer kuenftige Ansichten. Dass er nicht still zur
   * Sammelstelle wird, ist gemessen und nicht gehofft: der Waechter in
   * `test/routeReachability.test.ts` (Abschnitt 6b) montiert jede der drei
   * Ansichten und liest das GERENDERTE Manifest — eine Ansicht, die ihre
   * Angabe verliert, faellt dort namentlich auf.
   */
  ansicht?: BildschirmAnsicht;
}

/** Die Palette des Kitchen TV — dieselbe wie im statischen Manifest. */
const FARBE = '#0f2560';

/**
 * Die Symbole — FUER ALLE DREI ANSICHTEN DIESELBEN, und das ist gesagt und
 * nicht geerbt (Umfang (a), berichtigt am 2026-08-11).
 *
 * Im Repo liegt genau EIN Satz Symbole (`dashboard/public/icons/kitchen-192.png`,
 * `kitchen-512.png`), und er ist auch der des statischen Manifests. Je Ansicht
 * ein eigenes Sinnbild waere eine Gestaltungsfrage des Auftraggebers; sie hier
 * durch erfundene Dateinamen vorwegzunehmen waere schlimmer als der geteilte
 * Satz: eine Symboladresse, hinter der keine Datei liegt, laesst das Geraet die
 * Anwendung ohne Symbol oder gar nicht installieren. Der Waechter haelt
 * deshalb fest, dass jede genannte Symboldatei im Auslieferungsverzeichnis
 * WIRKLICH LIEGT.
 *
 * Der Dateiname `kitchen-*` ist historisch und wird NICHT umbenannt: er steht
 * ebenso im statischen Manifest (`vite.config.ts`), das dieser Task
 * ausdruecklich unberuehrt laesst.
 *
 * ⚠ JEDE ADRESSE TRAEGT IHREN URSPRUNG — dieselbe Regel wie bei `start_url`,
 * `id` und `scope` und aus demselben Grund (Blob-Verweis, undurchsichtiger
 * Pfad, REQ-SC-076 Praez. 1 (a)). Ein `/icons/kitchen-192.png` fiele hier
 * STILL aus dem Manifest heraus, und das Geraet zeigte ein Symbol, das niemand
 * gewaehlt hat.
 */
function kanalSymbole(origin: string): ManifestIcon[] {
  return [
    { src: `${origin}/icons/kitchen-192.png`, sizes: '192x192', type: 'image/png' },
    { src: `${origin}/icons/kitchen-512.png`, sizes: '512x512', type: 'image/png', purpose: 'any maskable' },
  ];
}

/**
 * Der Name, unter dem dieser Kanal auf dem Geraet erscheint.
 *
 * Faellt der Kanalname aus (leeres Feld, Dokument noch nicht eingetroffen),
 * tritt der Kanalschluessel an seine Stelle — ein Manifest OHNE Kanalbezug gibt
 * es nicht, denn genau der ist der Gegenstand dieses Tasks.
 */
export function kanalAnzeigeName(eingabe: KanalManifestEingabe): string {
  const name = (eingabe.name ?? '').trim();
  return name === '' ? eingabe.printerId : name;
}

/**
 * Das Anwendungs-Manifest genau dieses Kanals und genau dieser Ansicht
 * (REQ-SC-076 samt Praez. 2).
 *
 * ── DIE ENTSCHEIDUNGEN, EINZELN UND MIT GRUND ──────────────────────────────
 * • `name` / `short_name` folgen dem Bestand 1:1 (`route.ts:22-24`:
 *   `Kitchen TV - <Name>` und `Kitchen <Name>`; `queue/.../route.ts:31-33`:
 *   `Abholscreen - <Name>`). Der Auftraggeber-Entscheid vom 2026-08-03 lautet
 *   „Funktionsumfang: alles 1:1". Die eine benannte Abweichung (`short_name`
 *   der Fast-Line-Ansicht) ist an `BILDSCHIRM_ANSICHTSFORM` begruendet.
 * • KEIN `description`. Der Bestand fuehrt dort einen deutschen Satz. Eine fest
 *   verdrahtete Prosa waere benutzergerichteter Text ausserhalb von vue-i18n
 *   (REQ-OV-002) — und das Manifest ist ausdruecklich NICHT an die
 *   i18n-Engstelle dieses Meilensteins angeschlossen. Der Kanalname ist ein
 *   Eigenname und kein uebersetzter Text; er bleibt.
 * • `display: 'fullscreen'` statt des Bestandswerts `standalone`. REQ-SC-076
 *   sagt woertlich „(Vollbild, Querformat)", und `fullscreen` ist der Wert, der
 *   das bedeutet. `display_override` haelt `standalone` als Rueckfall — auf
 *   einer Umgebung ohne Vollbild verhaelt sich die Anwendung damit genau wie
 *   der Bestand, statt gar nicht installierbar zu sein.
 * • `scope` ist der Geltungsbereich DER ANSICHT (`/kitchen/`, `/abholscreen/`,
 *   `/fastline/`) und NICHT `/` (so der Bestand). Ein Geltungsbereich `/` zoege
 *   die Verwaltungsoberflaeche in die installierte Anwendung hinein; das ist
 *   genau das, was Akzeptanzkriterium 3 von T-M12.12 ausschliesst — und der
 *   Wert des statischen Manifests, unter dem Abholscreen und Fast Line bis zum
 *   2026-08-11 liefen. Er ist auch nicht auf `/kitchen/<id>` verengt:
 *   Geltungsbereiche vergleichen sich als Zeichenketten-Praefix, `/kitchen/abc`
 *   deckte damit auch `/kitchen/abcd` ab — eine Genauigkeit, die es nicht gibt,
 *   sollte man nicht vortaeuschen.
 *   ⚠ ERGAENZT AM 2026-08-22 (T-M25.1, REQ-SC-103 (3)) — berichtigt statt
 *   geloescht (REQ-OPS-036 Praez. 6): fuer die LIGHT-Adresse gilt der Satz
 *   nicht, ihr Geltungsbereich ist die Adresse selbst. Der Grund ist ein
 *   anderer als oben: Kitchen TV und Kitchen TV Light desselben Kanals liegen
 *   im SELBEN Adressraum, und ein gemeinsames `/kitchen/` machte sie fuer das
 *   Geraet zu zwei Anwendungen mit demselben Geltungsbereich. Die Rechnung
 *   steht in `BILDSCHIRM_ANSICHTSFORM.manifestScope` — je Ansicht eine
 *   Angabe, nicht eine Fallunterscheidung hier.
 * • `id` traegt die Adresse der Ansicht dieses Kanals. Ohne sie unterscheidet
 *   die Plattform zwei installierte Anwendungen desselben Ursprungs allein an
 *   der Startadresse; mit ihr ist „je Kanal eine eigenstaendige Anwendung"
 *   ausdruecklich gesagt — und zwar auch zwischen den DREI Ansichten desselben
 *   Kanals, die sonst dieselbe Kennung truegen.
 */
export function kanalManifest(eingabe: KanalManifestEingabe): KanalManifest {
  const anzeige = kanalAnzeigeName(eingabe);
  const ansicht = eingabe.ansicht ?? 'kitchen';
  const form    = BILDSCHIRM_ANSICHTSFORM[ansicht];
  const origin  = eingabe.origin.replace(/\/+$/, '');
  const adresse = `${origin}${bildschirmPfad(ansicht, eingabe.printerId)}`;

  return {
    id:               adresse,
    name:             form.name(anzeige),
    short_name:       form.shortName(anzeige),
    start_url:        adresse,
    scope:            `${origin}${form.manifestScope(eingabe.printerId)}`,
    display:          'fullscreen',
    display_override: ['fullscreen', 'standalone'],
    orientation:      'landscape',
    lang:             'de-CH',
    theme_color:      FARBE,
    background_color: FARBE,
    icons:            kanalSymbole(origin),
  };
}

// ═════════════════════════════════════════════════════════════════════════════
// Verdrahtung im Browser — ab hier ist es keine reine Funktion mehr
//
// Alles unterhalb dieser Linie beruehrt DOM und Navigator. Es ist bewusst
// duenn gehalten und faellt in jeder Umgebung ohne die noetigen Bausteine
// stillschweigend zurueck (Test-Umgebung, Entwicklungsserver ohne gebauten
// Dienst-Arbeiter, Browser ohne Unterstuetzung). Der Nachweis liegt auf der
// reinen Funktion darueber und auf dem statischen Waechter — nicht hier.
// ═════════════════════════════════════════════════════════════════════════════

let laufendeBlobAdresse: string | null = null;

/**
 * Haengt das je Kanal erzeugte Manifest an den `<link rel="manifest">` der
 * Seite — es tritt damit an die Stelle des statischen Manifests aus dem Bau.
 *
 * Rueckgabe: ob es gesetzt werden konnte. Die vorige Blob-Adresse wird
 * freigegeben, damit ein Bildschirm, der tagelang laeuft und den Kanalnamen
 * nachgefuehrt bekommt, keinen Speicher ansammelt.
 */
export function setzeKanalManifest(manifest: KanalManifest): boolean {
  if (typeof document === 'undefined') return false;
  if (typeof Blob !== 'function') return false;
  if (typeof URL === 'undefined' || typeof URL.createObjectURL !== 'function') return false;

  let link = document.querySelector<HTMLLinkElement>('link[rel="manifest"]');
  if (!link) {
    link = document.createElement('link');
    link.rel = 'manifest';
    document.head.appendChild(link);
  }

  const blob = new Blob([JSON.stringify(manifest)], { type: 'application/manifest+json' });
  const neu  = URL.createObjectURL(blob);
  if (laufendeBlobAdresse) URL.revokeObjectURL(laufendeBlobAdresse);
  laufendeBlobAdresse = neu;
  link.href = neu;
  return true;
}

/**
 * Registriert den Dienst-Arbeiter fuer GENAU EINEN Bildschirm-Geltungsbereich.
 *
 * ⚠ DIE SELBSTTAETIGE REGISTRIERUNG DES PLUGINS IST DAFUER ABGESCHALTET
 * (`injectRegister: false` in `vite.config.ts`). Sie fuegte das Registrierungs-
 * Skript in `index.html` ein, und `index.html` wird fuer JEDE Route
 * ausgeliefert — der Dienst-Arbeiter galt damit fuer die ganze Anwendung
 * (Geltungsbereich `/`).
 *
 * ── DIE BENANNTE FOLGE UND IHRE BERICHTIGUNG (T-M12.44, 2026-08-11) ─────────
 * Bis zum 2026-08-11 stand hier: „Keine Anforderung verlangt eine
 * Verwaltungs-PWA." **Der Satz ist falsch.** Das Umsetzungskonzept nennt die
 * PWA-Eigenschaft an drei Stellen, einmal ausdruecklich fuer die
 * Verwaltungsoberflaeche; die Anforderung heisst seit dem 2026-08-11
 * **REQ-OV-009**. Die Quelle steht dort und wird hier nicht abgeschrieben.
 *
 * **Und die Wirkung reichte weiter, als sie gemeldet war:** ohne
 * Dienst-Arbeiter waren nicht nur die Verwaltungsansichten, sondern auch der
 * ABHOLSCREEN und die FAST-LINE-Ansicht. Beide laufen auf denselben
 * Kiosk-Geraeten wie der Kitchen TV und sind nach **REQ-SC-006/007** die
 * RUECKFALL-SICHT, wenn der Drucker ausfaellt — eine Sicht, die nach einem
 * Netzunterbruch nicht mehr laedt, ist genau dann fort, wenn sie gebraucht
 * wird. T-M12.44 stellt sie wieder her.
 *
 * **Die Verwaltungsoberflaeche bleibt ohne Dienst-Arbeiter** — sie haengt an
 * OP-132 und wird nicht nebenbei mitentschieden (siehe `BILDSCHIRM_SW_SCOPES`).
 *
 * Fehlschlaege sind erwartbar und still: auf dem Entwicklungsserver gibt es
 * `/sw.js` nicht, in der Testumgebung keinen `navigator.serviceWorker`, und in
 * einem unsicheren Kontext ist die Registrierung verboten. Keiner dieser Faelle
 * darf eine Bildschirm-Sicht stoeren — sie funktioniert ohne Dienst-Arbeiter
 * vollstaendig, sie ist dann nur nicht installierbar.
 */
async function registriereBildschirmServiceWorker(scope: BildschirmSwScope): Promise<boolean> {
  if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) return false;
  try {
    await navigator.serviceWorker.register(KITCHEN_SW_URL, { scope });
    return true;
  } catch {
    return false;
  }
}

/**
 * Die drei Einstiege — je Ansicht einer, mit fest zugeordnetem
 * Geltungsbereich.
 *
 * Sie sind AUSDRUECKLICH drei benannte Funktionen und nicht ein Aufruf mit
 * freiem Argument: der Waechter in `test/routeReachability.test.ts` haelt die
 * Zuordnung Ansicht → Geltungsbereich, und eine Ansicht, die ihre Registrierung
 * verliert, faellt dort namentlich auf. Ein freies Argument liesse sich still
 * verstellen.
 */
export function registriereKitchenServiceWorker(): Promise<boolean> {
  return registriereBildschirmServiceWorker(KITCHEN_SW_SCOPE);
}

export function registriereAbholscreenServiceWorker(): Promise<boolean> {
  return registriereBildschirmServiceWorker(ABHOLSCREEN_SW_SCOPE);
}

export function registriereFastlineServiceWorker(): Promise<boolean> {
  return registriereBildschirmServiceWorker(FASTLINE_SW_SCOPE);
}
